import { useMemo, useState } from "react";
import { StyleSheet, Text, View } from "react-native";
import { useTranslation } from "react-i18next";

import { Button } from "../primitives/Button";
import type { ProgressDeps } from "../Progress/types";
import { useBreakpoint } from "../useBreakpoint";
import { useMasterDetailSlots } from "../useMasterDetailSlots";
import { WorkoutsView } from "../Workouts/WorkoutsView";
import { CoachSessionDetail } from "./CoachSessionDetail";
import type { AthleteSessionsList } from "./useAthleteSessions";

// #25: the athlete's sessions on the coach's athlete page, the shared
// WorkoutsView list read-only (no repeat, no delete). A host that passes
// `onOpenSession` opens the session itself (mobile pushes a route); without
// it the section is a master-detail of its own, like the web Workouts
// console: list beside the session when wide, the session in place of the
// list with a back button when narrow.

const LIST_WIDTH = 360;

export type AthleteSessionsSectionProps = {
  deps: ProgressDeps;
  sessions: AthleteSessionsList;
  onOpenSession?: (sessionId: string) => void;
};

export function AthleteSessionsSection({
  deps,
  sessions,
  onOpenSession,
}: AthleteSessionsSectionProps) {
  const { t } = useTranslation();
  const { theme } = deps;
  const styles = useMemo(() => makeStyles(theme), [theme]);
  const { wide } = useBreakpoint();
  const [collapsedMonths, setCollapsedMonths] = useState<Set<string>>(
    () => new Set(),
  );
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const inline = onOpenSession === undefined;
  const items = useMemo(
    () => sessions.items?.map((r) => r.session) ?? null,
    [sessions.items],
  );
  const selected =
    inline && selectedId !== null
      ? (sessions.items?.find((r) => r.session.id === selectedId) ?? null)
      : null;
  const slots = useMasterDetailSlots(wide, selected !== null);
  const showList = inline ? slots.showList : true;
  const showDetail = inline && slots.showDetail;

  const toggleMonth = (key: string) =>
    setCollapsedMonths((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const list = (
    <WorkoutsView
      theme={theme}
      items={items}
      error={
        sessions.error
          ? t("coach.sessions.loadFailed", {
              defaultValue: "Couldn't load the sessions.",
            })
          : null
      }
      refreshing={sessions.refreshing}
      loadingMore={sessions.loadingMore}
      cursor={sessions.cursor}
      collapsedMonths={collapsedMonths}
      onToggleMonth={toggleMonth}
      onRefresh={sessions.onRefresh}
      onEndReached={sessions.onEndReached}
      onSelectSession={onOpenSession ?? setSelectedId}
      selectedId={inline ? selectedId : null}
      enableRefreshControl={false}
      nested
    />
  );

  return (
    <View style={styles.wrap}>
      <Text style={styles.title} accessibilityRole="header">
        {t("coach.sessions.title", { defaultValue: "Sessions" })}
      </Text>
      <View style={wide && showDetail ? styles.row : undefined}>
        {showList ? (
          <View style={wide && showDetail ? styles.listPane : undefined}>
            {list}
          </View>
        ) : null}
        {showDetail ? (
          <View style={wide ? styles.detailPane : styles.detailNarrow}>
            {inline && slots.showBack ? (
              <View style={styles.backRow}>
                <Button
                  theme={theme}
                  variant="secondary"
                  icon="chevron-left"
                  label={t("mobile.common.back")}
                  onPress={() => setSelectedId(null)}
                />
              </View>
            ) : null}
            {selected ? (
              <CoachSessionDetail
                key={selected.session.id}
                deps={deps}
                entry={selected}
              />
            ) : (
              <Text style={styles.prompt}>
                {t("mobile.workouts.selectPrompt")}
              </Text>
            )}
          </View>
        ) : null}
      </View>
    </View>
  );
}

function makeStyles(theme: ProgressDeps["theme"]) {
  const { palette: p, spacing: s, typography: ty } = theme;
  return StyleSheet.create({
    wrap: { gap: s.s3 },
    title: { color: p.ink, fontSize: ty.sizes.md, fontWeight: "700" },
    row: { flexDirection: "row", alignItems: "flex-start", gap: s.s5 },
    listPane: { width: LIST_WIDTH },
    detailPane: { flex: 1, minWidth: 0 },
    detailNarrow: { gap: s.s3 },
    backRow: { flexDirection: "row" },
    prompt: { color: p.ink2, fontSize: ty.sizes.sm, padding: s.s5 },
  });
}
